import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../utils/axios";

interface AdminProfile {
  id: number;
  username: string;
  email: string;
  role: string;
}

const AdminAccount = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    securityCode: "",
  });

  /* ================= FETCH PROFILE ================= */
  const fetchProfile = async () => {
    try {
      const res = await api.get("/profile");
      setProfile(res.data.data);
    } catch {
      toast.error("Failed to load admin profile");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  /* ================= PASSWORD CHANGE ================= */
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    const { oldPassword, newPassword, confirmPassword, securityCode } = password;

    if (!oldPassword || !newPassword || !confirmPassword || !securityCode) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setPasswordLoading(true);

    try {
      const res = await api.post("/changepassword", {
        oldPassword,
        newPassword,
        confirmPassword,
        securityCode,
        token: localStorage.getItem("token") || "",
      });

      toast.success(res.data.message || "Password updated");
      handleLogout();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Password update failed");
    } finally {
      setPasswordLoading(false);
    }
  };

  /* ================= RESTORE DATABASE ================= */
  const handleRestoreDatabase = async () => {
    if (!window.confirm("This will restore the database. Continue?")) return;

    setRestoreLoading(true);

    try {
      const res = await api.post("/restoreDatabase");
      toast.success(res.data.message || "Database restored");
      handleLogout();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Restore failed");
    } finally {
      setRestoreLoading(false);
    }
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="mx-auto  space-y-8">
      {/* HEADER */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Admin Account</h1>
          <p className="text-sm text-gray-500">
            Manage your profile and security settings
          </p>
        </div>
      </header>

      {/* PROFILE */}
      <section className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Profile Information</h2>

        {profile ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Info label="Admin Name" value={profile.username} />
            <Info label="Email" value={profile.email} />
            <Info label="Role" value={profile.role} />
            {/* <Info label="Admin ID" value={`#${profile.id}`} /> */}
          </div>
        ) : (
          <p className="text-gray-500">Loading profile...</p>
        )}
      </section>

      {/* SECURITY */}
      <section className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Change Password</h2>

        <form
          onSubmit={handlePasswordChange}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <PasswordInput
            placeholder="Current password"
            onChange={(v) => setPassword({ ...password, oldPassword: v })}
          />
          <PasswordInput
            placeholder="New password"
            onChange={(v) => setPassword({ ...password, newPassword: v })}
          />
          <PasswordInput
            placeholder="Confirm new password"
            onChange={(v) =>
              setPassword({ ...password, confirmPassword: v })
            }
          />
          <PasswordInput
            placeholder="Security code"
            onChange={(v) =>
              setPassword({ ...password, securityCode: v })
            }
          />

          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={passwordLoading}
              className="px-6 py-2 rounded-lg bg-gray-900 text-white disabled:opacity-50"
            >
              {passwordLoading ? "Updating..." : "Change Password"}
            </button>
          </div>
        </form>
      </section>

      {/* DANGER ZONE */}
      <section className="border border-red-200 bg-red-50 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-red-700 mb-2">
          Danger Zone
        </h2>
        <p className="text-sm text-red-600 mb-4">
          Restoring the database will log you out and overwrite data.
        </p>

        <button
          onClick={handleRestoreDatabase}
          disabled={restoreLoading}
          className="px-6 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50"
        >
          {restoreLoading ? "Restoring..." : "Restore Database"}
        </button>
      </section>

      {/* LOGOUT */}
      <div className="flex justify-end">
        <button
          onClick={handleLogout}
          className="px-6 py-2 rounded-lg border text-red-600 hover:bg-red-50"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

/* ================= SMALL REUSABLE UI ================= */
const Info = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-gray-500">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

const PasswordInput = ({
  placeholder,
  onChange,
}: {
  placeholder: string;
  onChange: (value: string) => void;
}) => (
  <input
    type="password"
    placeholder={placeholder}
    className="border rounded-lg px-4 py-2"
    onChange={(e) => onChange(e.target.value)}
  />
);

export default AdminAccount;
